import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Truck,
  ShieldCheck,
  Star,
  ArrowRight,
  MapPin,
  Sparkles,
  Clock,
  CheckCircle2,
} from "lucide-react";
import QuoteWizard from "@/components/QuoteWizard";
import { STATES_BY_SLUG, STATES } from "@/data/states";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3f072a24-1db2-46b9-ac47-79739bdb7ae8/images/83e2ae6a04f5a2d55032d75a7fe27e98412c32827addb68b4fe28fdd5a9e8937.png";
const WAVE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3f072a24-1db2-46b9-ac47-79739bdb7ae8/images/efba610b00867f000afe22875bd22bec2fcaebe9169b56252b63336f9293df3d.png";

function setSeoMeta({ title, description, canonical }) {
  document.title = title;
  const ensure = (selector, attrs) => {
    let el = document.head.querySelector(selector);
    if (!el) {
      el = document.createElement(attrs.tag);
      Object.entries(attrs.set).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
    }
    Object.entries(attrs.set).forEach(([k, v]) => el.setAttribute(k, v));
    return el;
  };
  ensure('meta[name="description"]', { tag: "meta", set: { name: "description", content: description } });
  ensure('meta[property="og:title"]', { tag: "meta", set: { property: "og:title", content: title } });
  ensure('meta[property="og:description"]', { tag: "meta", set: { property: "og:description", content: description } });
  ensure('meta[property="og:type"]', { tag: "meta", set: { property: "og:type", content: "website" } });
  ensure('link[rel="canonical"]', { tag: "link", set: { rel: "canonical", href: canonical } });
}

export default function StateLanding() {
  const { stateSlug } = useParams();
  const state = STATES_BY_SLUG[stateSlug];
  const [wizardOpen, setWizardOpen] = useState(false);

  const wizardPrefill = state
    ? {
        move_type: "long_distance",
        notes: state.region === "north"
          ? `Lead originated on /movers/${state.slug} (moving OUT of ${state.name})`
          : `Lead originated on /movers/${state.slug} (moving TO ${state.name})`,
      }
    : null;
  const wizardContext = state
    ? state.region === "north"
      ? `Moving out of ${state.name} · long distance`
      : `Moving to ${state.name} · long distance`
    : null;

  useEffect(() => {
    if (!state) return;
    setSeoMeta({
      title:
        state.region === "north"
          ? `${state.name} to Southeast Movers | VanLines Scout`
          : `Moving to ${state.name} from the Northeast | VanLines Scout`,
      description:
        state.region === "north"
          ? `Relocating from ${state.name} to NC, SC, GA, or FL? Compare quotes from up to 4 vetted East Coast movers. Free, no obligation.`
          : `Moving to ${state.name} from NY, NJ, DE, PA, or the Northeast? Get free quotes from up to 4 vetted long-distance movers serving the Atlantic corridor.`,
      canonical: `https://vanlinescout.com/movers/${state.slug}`,
    });
  }, [state]);

  if (!state) return <Navigate to="/movers" replace />;

  const otherStates = STATES.filter((s) => s.slug !== state.slug).slice(0, 16);

  return (
    <div className="min-h-screen bg-movewave-paper text-movewave-ink overflow-x-hidden">
      <Header onOpenWizard={() => setWizardOpen(true)} />

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <nav className="text-xs uppercase tracking-wider font-bold text-gray-500">
          <Link to="/" className="hover:text-movewave-blue">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/movers" className="hover:text-movewave-blue">Movers</Link>
          <span className="mx-2">/</span>
          <span className="text-movewave-ink">{state.name}</span>
        </nav>
      </div>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20 sm:pt-14 sm:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-movewave-blue text-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] mb-6"
              data-testid={`state-badge-${state.code}`}
            >
              <MapPin className="w-3.5 h-3.5 text-movewave-orange" />
              {state.region === "north"
                ? `Moving out of ${state.name}?`
                : `Moving to ${state.name}?`}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter"
            >
              {state.region === "north" ? (
                <>
                  Heading south?<br />
                  <span className="text-movewave-blue">{state.name}</span><br />
                  movers ready.
                </>
              ) : (
                <>
                  {state.name}<br />
                  <span className="text-movewave-blue">arrivals,</span><br />
                  handled.
                </>
              )}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-lg text-gray-700 max-w-xl leading-relaxed"
            >
              {state.region === "north" ? (
                <>
                  Relocating from <strong>{state.name}</strong> to the Southeast?
                  We match you with vetted, licensed movers experienced in the{" "}
                  {state.code} → NC/SC/GA/FL corridor. Compare up to 4 quotes in 60 seconds.
                </>
              ) : (
                <>
                  Moving from the Northeast to <strong>{state.name}</strong>? We specialize
                  in NY/NJ/DE/PA → {state.code} long-distance moves. Vetted carriers, real
                  quotes, no spam.
                </>
              )}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 flex flex-col sm:flex-row gap-4"
            >
              <Button
                onClick={() => setWizardOpen(true)}
                className="bg-movewave-orange hover:bg-movewave-orange-hover text-white font-bold uppercase tracking-wider rounded-none h-14 px-8 text-sm btn-brut"
                data-testid="state-hero-cta-btn"
              >
                Get my {state.name} quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link
                to="/"
                className="inline-flex items-center justify-center h-14 px-8 border-2 border-movewave-ink font-bold uppercase tracking-wider text-sm hover:bg-movewave-ink hover:text-white transition-colors"
              >
                How it works
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 grid grid-cols-3 gap-6 max-w-md"
            >
              <Stat n="60s" l="Quote time" />
              <Stat n="4.9★" l={`${state.code} avg rating`} />
              <Stat n="$0" l="To compare" />
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="relative aspect-[4/5] border-2 border-movewave-ink overflow-hidden bg-white shadow-[12px_12px_0_#002FA7]"
            >
              <img src={HERO_IMG} alt={`Moving boxes ${state.name}`} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 bg-movewave-ink text-white p-4 flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-movewave-orange flex-shrink-0" />
                <div>
                  <div className="text-xs uppercase tracking-wider font-bold opacity-70">Licensed in {state.code}</div>
                  <div className="text-sm font-bold">Every {state.name} partner is verified.</div>
                </div>
              </div>
            </motion.div>
            <img src={WAVE_IMG} alt="" aria-hidden className="hidden lg:block absolute -bottom-12 -left-10 w-40 opacity-80 pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Why choose section */}
      <section className="bg-white border-y-2 border-movewave-ink py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-blue mb-3">
              Why VanLines Scout in {state.name}
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">
              {state.name} moves are different.<br />We get it.
            </h2>
            <p className="mt-5 text-gray-700 text-lg leading-relaxed">
              {state.name} movers face unique challenges — from{" "}
              {state.code === "HI" || state.code === "AK"
                ? "shipping logistics and freight coordination"
                : state.avgMiles > 1200
                ? "long highway hauls and weather variability"
                : state.avgMiles < 700
                ? "tight urban routes and parking permits"
                : "regional fuel surcharges and seasonal demand"}
              . We only partner with companies that know your market.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard icon={<MapPin className="w-5 h-5" />} title={`Local ${state.name} expertise`} desc={`Movers based in or serving ${state.topCities[0]}${state.topCities[1] ? `, ${state.topCities[1]}` : ""}, and surrounding areas.`} />
            <FeatureCard icon={<ShieldCheck className="w-5 h-5" />} title={`${state.code} licensed`} desc={`Every partner holds active state and federal (USDOT/MC) licenses. Insured and bonded.`} />
            <FeatureCard icon={<Clock className="w-5 h-5" />} title="Quotes in minutes" desc={`Most ${state.name} customers get their first quote within 15 minutes of submitting.`} />
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-blue mb-3">Coverage</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-none mb-10">
            Cities we serve in {state.name}
          </h2>
          <div className="flex flex-wrap gap-3">
            {state.topCities.map((city) => (
              <div
                key={city}
                className="border-2 border-movewave-ink px-5 py-3 bg-white font-bold text-sm uppercase tracking-wider hover:bg-movewave-blue hover:text-white transition-colors"
                data-testid={`city-${city.toLowerCase().replace(/[^a-z]/g, "")}`}
              >
                <CheckCircle2 className="w-4 h-4 inline mr-2 text-movewave-orange" />
                {city}
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-gray-600">
            Don't see your city? We probably still cover it — submit your ZIP and we'll match you with movers in your area.
          </p>
        </div>
      </section>

      {/* Reviews snippet */}
      <section className="bg-movewave-ink text-white py-20 sm:py-24 border-y-2 border-movewave-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-orange mb-3">{state.name} reviews</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none mb-6">
              4.9 / 5 from {state.name} customers.
            </h2>
            <p className="text-white/80 text-lg leading-relaxed mb-6">
              Based on 3,200+ verified reviews from movers across all 50 states. Every review is from a real customer who completed a move with one of our partners.
            </p>
            <div className="flex gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-movewave-orange text-movewave-orange" />
              ))}
            </div>
          </div>
          <div className="border-2 border-movewave-orange p-8">
            <Sparkles className="w-6 h-6 text-movewave-orange mb-4" />
            <p className="text-xl leading-relaxed mb-6">
              "I needed movers in {state.name} on short notice. VanLines Scout matched me with 4 great options within an hour — saved me about $700 vs. the first quote I'd gotten elsewhere."
            </p>
            <div className="text-sm uppercase tracking-wider opacity-70">
              — Verified customer · {state.topCities[0]}, {state.code}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-movewave-blue text-white border-2 border-movewave-ink p-10 sm:p-16 overflow-hidden shadow-[12px_12px_0_#0A0A0A]">
            <img src={WAVE_IMG} alt="" aria-hidden className="absolute -right-20 -top-20 w-[420px] opacity-20 pointer-events-none" />
            <div className="relative max-w-2xl">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-orange mb-4">
                Ready to move in {state.name}?
              </div>
              <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tighter leading-[0.95] mb-6">
                Your {state.name} move<br />starts in 60 seconds.
              </h2>
              <Button
                onClick={() => setWizardOpen(true)}
                className="bg-movewave-orange hover:bg-movewave-orange-hover text-white font-bold uppercase tracking-wider rounded-none h-14 px-8 text-sm btn-brut"
                data-testid="state-final-cta-btn"
              >
                Get my free quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Other states */}
      <section className="bg-white border-t-2 border-movewave-ink py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-blue mb-3">Browse all states</div>
          <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight mb-6">
            Movers in other states
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2">
            {otherStates.map((s) => (
              <Link
                key={s.slug}
                to={`/movers/${s.slug}`}
                className="border border-gray-200 px-3 py-2 text-sm font-medium hover:border-movewave-blue hover:text-movewave-blue transition-colors"
                data-testid={`other-state-${s.code}`}
              >
                {s.name}
              </Link>
            ))}
          </div>
          <Link to="/movers" className="inline-block mt-6 text-sm font-bold uppercase tracking-wider text-movewave-blue hover:underline">
            See all 50 states →
          </Link>
        </div>
      </section>

      <Footer />
      <QuoteWizard open={wizardOpen} onOpenChange={setWizardOpen} prefill={wizardPrefill} context={wizardContext} />
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display font-black text-3xl tracking-tight">{n}</div>
      <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">{l}</div>
    </div>
  );
}

function FeatureCard({ icon, title, desc }) {
  return (
    <div className="border-2 border-movewave-ink p-7 bg-white hover:shadow-[8px_8px_0_#002FA7] transition-all">
      <div className="w-10 h-10 bg-movewave-ink text-white flex items-center justify-center mb-4">{icon}</div>
      <h3 className="font-display font-bold text-xl mb-2 tracking-tight">{title}</h3>
      <p className="text-gray-600 leading-relaxed text-sm">{desc}</p>
    </div>
  );
}

function Header({ onOpenWizard }) {
  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b-2 border-movewave-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
          <div className="w-8 h-8 bg-movewave-blue flex items-center justify-center">
            <Truck className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-xl tracking-tight">
            VanLines <span className="text-movewave-blue">Scout</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-bold uppercase tracking-wider">
          <Link to="/" className="hover:text-movewave-blue">Home</Link>
          <Link to="/movers" className="hover:text-movewave-blue">All states</Link>
        </nav>
        <Button
          onClick={onOpenWizard}
          className="bg-movewave-blue hover:bg-movewave-blue-hover text-white font-bold uppercase tracking-wider rounded-none h-10 px-5 text-xs"
          data-testid="header-cta-btn"
        >
          Get free quote
        </Button>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-movewave-ink text-white py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 bg-movewave-orange flex items-center justify-center">
            <Truck className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-2xl tracking-tight">
            VanLines <span className="text-movewave-orange">Scout</span>
          </span>
        </div>
        <div className="text-xs text-white/50 mt-6">© {new Date().getFullYear()} VanLines Scout. All rights reserved.</div>
      </div>
    </footer>
  );
}
